import eventrefsinfo from "../assets/refs";
import eventRefs from "../assets/eventRef";
import {
  CUSTOM_FUNCTION_ACTION_CODE,
  encodeCustomFunctionPresetById,
  getCustomFunctionPresetById,
  getCustomFunctionPresetLabel,
} from "../assets/customFunctionPresets";
import { isApiVersionGte } from "./deviceApiVersion";
import { formatInterfaceLabel, normalizeInterfaceKey } from "./networkInterfaces";

const { actionRefs } = eventrefsinfo;

export const buildDeviceAlertsPath = (enclosureSerialNumber) =>
  `/devices/connected/${enclosureSerialNumber}/alerts`;

export const buildAlertCreatePath = (enclosureSerialNumber) =>
  `${buildDeviceAlertsPath(enclosureSerialNumber)}/new`;

export const buildAlertDetailsPath = (enclosureSerialNumber, eventId) =>
  `${buildDeviceAlertsPath(enclosureSerialNumber)}/${eventId}`;

export const buildAlertEditPath = (enclosureSerialNumber, eventId) =>
  `${buildAlertDetailsPath(enclosureSerialNumber, eventId)}/edit`;

export const buildAlertLogsPath = (enclosureSerialNumber, eventId) =>
  `/devices/connected/${enclosureSerialNumber}/event-logs?eventId=${encodeURIComponent(
    eventId,
  )}`;

export function getEventRefByCode(eventCode) {
  return eventRefs.find((eventRef) => eventRef.code == eventCode) || null;
}

export function getEventParamCount(eventCode, eventRef, apiVersion) {
  return eventCode === 12 && !isApiVersionGte(apiVersion, "1.1")
    ? 2
    : eventRef?.params?.length || 0;
}

export function normalizeActionParams(params = []) {
  return [params[0] || "", params[1] || "", params[2] || ""];
}

export function getActionRefByCode(actionCode) {
  return actionRefs.find((action) => action.code == actionCode) || null;
}

export function getActionTypeLabel(actionCode) {
  const actionRef = getActionRefByCode(actionCode);
  return actionRef?.name || `Unknown Action (Code ${actionCode})`;
}

export function formatActionParamValue(actionCode, value, index) {
  if (actionCode === CUSTOM_FUNCTION_ACTION_CODE && index === 0) {
    return getCustomFunctionPresetLabel(value);
  }

  return value;
}

export function getActionParamRows(action = {}) {
  const actionRef = getActionRefByCode(action.code);
  const values = normalizeActionParams(action.params);

  if (actionRef?.params?.length) {
    return actionRef.params.map((label, index) => ({
      key: label,
      label,
      value: formatActionParamValue(action.code, values[index], index),
    }));
  }

  return values
    .map((value, index) => ({
      key: `param_${index + 1}`,
      label: `Param ${index + 1}`,
      value,
    }))
    .filter((row) => row.value);
}

export function validateCustomFunctionJsonPayload(value = "") {
  return value.length > 1000 ? "Must be 1000 characters or fewer" : "";
}

export function getDefaultCustomFunctionFormState() {
  return {
    replaceCustomFunctionScript: false,
    currentCustomFunctionScriptName: "",
  };
}

export function getActionFormStateForEditing(action = {}) {
  const normalizedParams = normalizeActionParams(action.params);

  if (action.code !== CUSTOM_FUNCTION_ACTION_CODE) {
    return {
      newActionParams: normalizedParams,
      ...getDefaultCustomFunctionFormState(),
    };
  }

  return {
    newActionParams: normalizedParams,
    replaceCustomFunctionScript: false,
    currentCustomFunctionScriptName: normalizedParams[0] || "",
  };
}

export function buildActionParamsForSave(
  actionCode,
  params = [],
  { isUpdate = false, replaceScript = false } = {},
) {
  if (actionCode !== CUSTOM_FUNCTION_ACTION_CODE) {
    return normalizeActionParams(params);
  }

  const normalizedParams = normalizeActionParams(params);
  const customFunctionParam = normalizedParams[0];
  const preset = getCustomFunctionPresetById(customFunctionParam);

  if (validateCustomFunctionJsonPayload(normalizedParams[1])) {
    return null;
  }

  if (isUpdate && !replaceScript) {
    return ["", normalizedParams[1]];
  }

  if (!preset) {
    return null;
  }

  return [
    encodeCustomFunctionPresetById(customFunctionParam),
    normalizedParams[1],
  ];
}

export function isEditableAction(action = {}) {
  return Boolean(getActionRefByCode(action.code));
}

function getNetworkInterfaceValue(rawValue) {
  if (rawValue === "" || rawValue === null || rawValue === undefined) {
    return "1";
  }

  return String(rawValue);
}

function getNetworkInterfaceLabel(rawValue, { forDescription = false } = {}) {
  const normalizedValue = getNetworkInterfaceValue(rawValue);
  const normalizedLower = normalizedValue.toLowerCase();

  if (normalizedLower === "default") {
    return forDescription ? "default route" : "Default route";
  }

  if (normalizedValue === "2") {
    return forDescription
      ? "USB secondary ethernet"
      : "Secondary Network Interface (USB-Ethernet)";
  }

  if (normalizedLower === "secondary") {
    return forDescription ? "USB secondary ethernet" : "USB Ethernet";
  }

  if (normalizedLower === "primary") {
    return forDescription ? "primary route" : "Primary route";
  }

  if (normalizedValue !== "1") {
    const interfaceKey = normalizeInterfaceKey(normalizedValue);
    if (interfaceKey) {
      return formatInterfaceLabel(interfaceKey);
    }
  }

  return forDescription
    ? "primary ethernet"
    : "Primary Network Interface";
}

export function formatEventParamValue(
  eventCode,
  paramDef,
  rawValue,
  index,
  { forDescription = false } = {},
) {
  if ((eventCode == 11 || eventCode == 12) && index === 2) {
    return getNetworkInterfaceLabel(rawValue, { forDescription });
  }

  if (paramDef?.options?.length) {
    const foundOption = paramDef.options.find(
      (option) => String(option.value) === String(rawValue),
    );

    if (foundOption) {
      return foundOption.label;
    }
  }

  if (rawValue === "" || rawValue === null || rawValue === undefined) {
    return "Not set";
  }

  return String(rawValue);
}

export function hydrateEventDescription(
  template = "",
  params = [],
  eventCode,
  eventRef = getEventRefByCode(eventCode),
) {
  if (!template) {
    return "";
  }

  return template.replace(/{{param(\d+)}}/g, (_, rawIndex) => {
    const index = parseInt(rawIndex, 10) - 1;
    const paramDef = eventRef?.params?.[index];
    return formatEventParamValue(eventCode, paramDef, params[index], index, {
      forDescription: true,
    });
  });
}

export function getEventDescription(
  event = {},
  { triggered = Boolean(event?.triggered) } = {},
) {
  const eventRef = getEventRefByCode(event?.code);
  const template = triggered
    ? eventRef?.triggeredDescription
    : eventRef?.notTriggeredDescription;

  return hydrateEventDescription(
    template,
    event?.params || [],
    event?.code,
    eventRef,
  );
}

export function getEventParameterRows(event = {}, apiVersion) {
  const eventRef = getEventRefByCode(event?.code);
  const params = Array.isArray(event?.params) ? event.params : [];
  const paramCount = Math.max(
    getEventParamCount(event?.code, eventRef, apiVersion),
    params.length,
  );

  return Array.from({ length: paramCount })
    .map((_, index) => {
      const paramDef = eventRef?.params?.[index];
      return {
        key: paramDef?.label || `param_${index + 1}`,
        label: paramDef?.label || `Param ${index + 1}`,
        hasParamDefinition: Boolean(paramDef),
        value: formatEventParamValue(
          event?.code,
          paramDef,
          params[index],
          index,
        ),
        unit: paramDef?.unit || "",
        rawValue: params[index],
      };
    })
    .filter(
      (row) =>
        row.hasParamDefinition ||
        (row.rawValue !== "" &&
          row.rawValue !== null &&
          row.rawValue !== undefined),
    );
}
